import { useMemo, useState } from "react";
import type { CellContext } from "@tanstack/react-table";
import { t } from "@/i18n-js/instance";
import type { AgentProp } from "@circle-react/components/SettingsApp/CommunityBot/Agents";
import {
  CommunityMemberColumn,
  CommunityMembersListProvider,
} from "@circle-react/components/SettingsApp/Workflows/components/CommunityMemberColumn";
import { ActionGroup } from "@circle-react/components/SettingsApp/Workflows/components/NewWorkflowV2/ActionGroup";
import { Datetime } from "@circle-react/components/SettingsApp/Workflows/components/Table/Datetime";
import { WorkflowName } from "@circle-react/components/SettingsApp/Workflows/components/Table/WorkflowName";
import { WORKFLOW_STATUS } from "@circle-react/components/SettingsApp/Workflows/constants";
import type { ListWorkflows } from "@circle-react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { workflowsSettingsPaths } from "@circle-react/helpers/urlHelpers";
import { InfiniteQueryDataTable } from "@circle-react-shared/InfiniteQueryDataTable";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Button } from "@circle-react-uikit/Button";
import { DataTable } from "@circle-react-uikit/DataTableV2";
import { BotActiveToggle } from "./components/BotActiveToggle";
import {
  DEFAULT_PAGE_SIZE,
  useBotWorkflowsQuery,
} from "./hooks/useBotWorkflowsQuery";

type WorkflowRecord = NonNullable<ListWorkflows["records"]>[number];

interface BotWorkflowsTableProps {
  agent: AgentProp;
  onNewWorkflow: () => void;
}

const i18nRoot = "settings.workflows.table";

const columnStyles = {
  // eslint-disable-next-line no-restricted-syntax -- Not a translatable string
  title: "w-[260px] min-w-[260x]",
  steps: "w-[240px] min-w-[240px]",
  user: "w-[120px] min-w-[120px]",
  dateShort: "w-[100px] min-w-[100px]",
  dateLong: "w-[140px] min-w-[140px]",
  number: "w-[80px] min-w-[80px]",
  toggleInput: "w-[60px] min-w-[60px]",
};

const renderWorkflowName = (info: CellContext<WorkflowRecord, string>) => (
  <WorkflowName>
    {info.getValue() || t([i18nRoot, "untitled_workflow"])}
  </WorkflowName>
);

const renderActionGroup = (info: CellContext<WorkflowRecord, string[]>) => (
  <ActionGroup
    actions={info.getValue()}
    size="md"
    truncationLimit={3}
    className="flex-wrap py-4"
  />
);

const renderCommunityMember = (info: CellContext<WorkflowRecord, number>) => (
  <CommunityMemberColumn id={info.getValue()} isLastNameHidden />
);

const renderCreatedDate = (info: CellContext<WorkflowRecord, string>) => (
  <Datetime date={info.getValue()} shouldIncludeTime={false} />
);

const renderLastRunDate = (
  info: CellContext<WorkflowRecord, string | null>,
) => <Datetime date={info.getValue()} shouldIncludeTime />;

const renderTasksCount = (info: CellContext<WorkflowRecord, number>) => (
  <span className="break-normal">{info.getValue() || 0}</span>
);

const renderActiveToggle = (
  info: CellContext<WorkflowRecord, unknown>,
  agentId: number,
) => {
  const workflow = info.row.original;

  const workflowWithNonNullStatus = {
    ...workflow,
    status: workflow.status || "inactive",
  };
  return (
    <BotActiveToggle workflow={workflowWithNonNullStatus} agentId={agentId} />
  );
};

export const BotWorkflowsTable = ({
  agent,
  onNewWorkflow,
}: BotWorkflowsTableProps) => {
  const [page, setPage] = useState(1);

  const infiniteQueryResult = useBotWorkflowsQuery({
    agentId: agent.id,
    initialPage: page,
    perPage: DEFAULT_PAGE_SIZE,
  });

  const { data, isLoading } = infiniteQueryResult;

  const workflows = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.records || []);
  }, [data?.pages]);

  const totalWorkflows = data?.pages?.[0]?.total_count || 0;

  const communityMemberIds = useMemo(() => {
    if (isLoading || !workflows) return null;
    return workflows.map(
      ({ created_by_community_member_id }) => created_by_community_member_id,
    );
  }, [workflows, isLoading]);

  const onClickRowHandler = ({ original }: { original: WorkflowRecord }) => {
    const { id: workflowId, status } = original || {};
    if (!workflowId) return;

    const isDraft = status === WORKFLOW_STATUS.DRAFT;
    const targetPath = isDraft
      ? workflowsSettingsPaths.edit({ workflowId })
      : workflowsSettingsPaths.view({ workflowId });

    window.location.href = targetPath;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t([i18nRoot, "name"]),
        thClassName: columnStyles.title,
        cellClassName: columnStyles.title,
        cell: renderWorkflowName,
      },
      {
        accessorKey: "steps",
        header: t([i18nRoot, "steps"]),
        thClassName: columnStyles.steps,
        cellClassName: classNames("!py-0", columnStyles.steps),
        cell: renderActionGroup,
      },
      {
        accessorKey: "created_by_community_member_id",
        header: t([i18nRoot, "created_by"]),
        thClassName: columnStyles.user,
        cellClassName: columnStyles.user,
        cell: renderCommunityMember,
      },
      {
        accessorKey: "created_at",
        header: t([i18nRoot, "created_on"]),
        thClassName: columnStyles.dateShort,
        cellClassName: columnStyles.dateShort,
        cell: renderCreatedDate,
      },
      {
        accessorKey: "last_run_at",
        header: t([i18nRoot, "last_run"]),
        thClassName: columnStyles.dateLong,
        cellClassName: columnStyles.dateLong,
        cell: renderLastRunDate,
      },
      {
        accessorKey: "tasks_count",
        header: t([i18nRoot, "total_runs"]),
        thClassName: columnStyles.number,
        thInnerClassName: "justify-end",
        cellClassName: classNames("text-right", columnStyles.number),
        cell: renderTasksCount,
      },
      {
        header: t([i18nRoot, "active"]),
        thClassName: columnStyles.toggleInput,
        thInnerClassName: "justify-center",
        cellClassName: classNames(
          "!py-4 cursor-default",
          columnStyles.toggleInput,
        ),
        onClick: ({ event }: { event: React.MouseEvent }) =>
          event.stopPropagation(),
        cell: (info: CellContext<WorkflowRecord, unknown>) =>
          renderActiveToggle(info, agent.id),
      },
    ],
    [agent.id],
  );

  const infiniteQuery = {
    ...infiniteQueryResult,
    data: { pages: data?.pages || [] },
    workflows,
  };

  return (
    <div className="bg-primary flex h-full flex-col">
      <div className="bg-primary border-primary flex items-center justify-start gap-2 border-b px-6 py-5">
        <Typography.LabelLg weight="semibold">
          {t("community_bot.bot_builder.automations_page.empty_state.title")}
        </Typography.LabelLg>
      </div>

      <div className="flex items-center justify-between px-6 py-4">
        <span className="text-dark text-sm font-semibold">
          {t([i18nRoot, "workflows_count"], { count: totalWorkflows })}
        </span>
        <Button
          type="button"
          variant="circle"
          onClick={onNewWorkflow}
          disabled={!agent.enabled}
        >
          {t(
            "community_bot.bot_builder.automations_page.empty_state.new_workflow_button",
          )}
        </Button>
      </div>

      <div className="flex-1 px-6 pb-6">
        <CommunityMembersListProvider ids={communityMemberIds ?? undefined}>
          <div className="-mx-6 lg:-mx-9">
            <InfiniteQueryDataTable
              columns={columns}
              infiniteQuery={infiniteQuery}
              pageSize={DEFAULT_PAGE_SIZE}
              initialPage={page}
              totalCountPath="total_count"
              onChangePage={(newPage: number) => setPage(newPage)}
              onClickRow={onClickRowHandler}
            >
              <DataTable.TableWrapper>
                <DataTable.Table>
                  <DataTable.Header className="z-10" />
                  <DataTable.Loader />
                  <DataTable.NoRecords
                    text={t([i18nRoot, "no_workflows_found"])}
                  />
                  <DataTable.Body />
                </DataTable.Table>
                <DataTable.Pagination
                  align="left"
                  direction="reverse"
                  buttonType="button"
                />
              </DataTable.TableWrapper>
            </InfiniteQueryDataTable>
          </div>
        </CommunityMembersListProvider>
      </div>
    </div>
  );
};
